import {
  Button,
  DismissIcon,
  EyeIcon,
  Flex,
  HideEyeIcon,
  Link,
  TextField,
  Typography,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import type { MouseEvent } from 'react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Form } from 'react-router';

import { BloomLink } from '@jane/bloom/components/bloomLink';
import { useLogin } from '@jane/bloom/hooks/account/useLogin';
import type { CheckoutAuthFormStep } from '@jane/bloom/routes/_menu.checkout._index/types';
import { AuthErrorMessage } from '@jane/bloom/types/schemas/marketplace/authErrors';

import styles from './loginModal.module.css';

const migratedUserErrorMessage =
  'We recently made some changes to the site. If you are unable to login, you may need to reset your password.';

interface WelcomeProps {
  bottomText?: string;
  isBloomMessagingForMigratedUsersEnabled: boolean;
  onClose: () => void;
  setFormStep: (step: CheckoutAuthFormStep) => void;
  skipBagAfterAuth: boolean;
  userEmail: string;
}

export const Welcome = ({
  bottomText,
  isBloomMessagingForMigratedUsersEnabled,
  onClose,
  setFormStep,
  skipBagAfterAuth,
  userEmail,
}: WelcomeProps) => {
  const [visible, setVisible] = useState(false);
  const [showPasswordError, setShowPasswordError] = useState(false);
  const { fetcher, login } = useLogin({ skipBagAfterAuth });
  const [visibleMigratedMessage, setVisibleMigratedMessage] = useState(false);

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    setError,
  } = useForm({
    defaultValues: {
      password: '',
    },
    mode: 'all',
  });

  const onForgotPassword = (e: MouseEvent) => {
    e.preventDefault();
    setFormStep('forgotPassword');
  };

  const submitSignInForm = ({ password }: { password: string }) => {
    login({ email: userEmail, password });
  };

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data && !fetcher.data.success) {
      setShowPasswordError(true);
      switch (fetcher.data.errors['message'][0]) {
        case AuthErrorMessage.INVALID_USER_OR_PASSWORD:
        case AuthErrorMessage.USER_ALMOST_LOCKED:
          setError('password', {
            message: fetcher.data.errors['message'][0],
          });
          setVisibleMigratedMessage(true);
          break;
        case AuthErrorMessage.USER_LOCKED:
          setError('root', {
            message: fetcher.data.errors['message'][0],
          });
          setVisibleMigratedMessage(true);
          break;
        default:
          break;
      }
    }

    document.getElementById('password')?.addEventListener('keydown', () => {
      setShowPasswordError(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher.state]);

  return (
    <Flex flexDirection="column">
      <Button.Icon
        className={styles['closeButton']}
        icon={<DismissIcon />}
        label="close"
        onClick={onClose}
      />
      <Typography variant="header" textAlign="center">
        Welcome back!
      </Typography>
      <Form
        name="Account Sign In Form"
        className={styles['form']}
        onSubmit={handleSubmit(submitSignInForm)}
      >
        <Flex className={styles['emailContainer']}>
          <Typography variant="body-bold" color="grays-black" mr={8}>
            {userEmail}
          </Typography>
          <Link onClick={() => setFormStep('signIn')}>Edit</Link>
        </Flex>
        <Flex flexDirection="column">
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <TextField
                disableMobileInputStyling
                autocomplete="off"
                type={visible ? 'text' : 'password'}
                label="Password"
                name="password"
                mb={8}
                className={clsx({
                  [styles['errorPassTextField']]:
                    showPasswordError && errors.password,
                })}
                onChange={(password) => {
                  field.onChange(password);
                }}
                onBlur={() => {
                  setShowPasswordError(true);
                }}
                endIcon={
                  visible ? (
                    <EyeIcon
                      altText="Hide password"
                      onClick={() => setVisible(false)}
                    />
                  ) : (
                    <HideEyeIcon
                      altText="Show password"
                      onClick={() => setVisible(true)}
                    />
                  )
                }
              />
            )}
          />
          {errors.password && showPasswordError && (
            <Typography variant="mini" color="error">
              {errors.password.message}
            </Typography>
          )}
          <BloomLink
            className={styles['forgotPassContainer']}
            to=""
            onClick={onForgotPassword}
            variant="reefer-underline"
          >
            Forgot password?
          </BloomLink>
          <Button
            label="Sign in"
            full
            type="submit"
            disabled={!isValid}
            mt={16}
            loading={fetcher.state !== 'idle'}
            variant="primary"
          />
          {!!bottomText && (
            <Typography variant="body" textAlign="center" mt={16}>
              {bottomText}
            </Typography>
          )}
          {isBloomMessagingForMigratedUsersEnabled &&
            visibleMigratedMessage && (
              <Typography
                variant="body"
                color="grays-black"
                mt={24}
                className={styles['lockedAccountMessage']}
              >
                {migratedUserErrorMessage}
              </Typography>
            )}
          {errors.root && showPasswordError && (
            <Typography
              variant="body"
              color="error"
              mt={visibleMigratedMessage ? 16 : 24}
              className={styles['lockedAccountMessage']}
            >
              {errors.root.message}
            </Typography>
          )}
        </Flex>
      </Form>
    </Flex>
  );
};
